import { useState, useEffect, useCallback } from "react";
import {
  Users,
  UserCheck,
  LogIn,
  Smile,
  Moon,
  Heart,
  Droplets,
  Watch,
  ClipboardList,
  TrendingUp,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  ShieldAlert,
  Timer,
} from "lucide-react";
import { ErrorAlert } from "../components/ErrorAlert";
import dashboardApi from "../api/dashboard";
import { useAuth } from "../context/AuthContext";

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 animate-pulse">
      <div className="w-10 h-10 rounded-xl bg-gray-200 mb-3" />
      <div className="h-6 bg-gray-200 rounded w-1/2 mb-1" />
      <div className="h-3 bg-gray-100 rounded w-3/4" />
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color, bg, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${bg}`}
      >
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className={`font-display font-800 text-2xl ${color}`}>{value ?? 0}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
      {children}
    </p>
  );
}

// ── Time ago ──────────────────────────────────────────────────────────────────
function timeAgo(date) {
  if (!date) return null;
  const diff = Math.floor((new Date() - date) / 1000);
  if (diff < 60) return "Baru saja";
  if (diff < 3600) return `${Math.floor(diff / 60)} menit yang lalu`;
  return `${Math.floor(diff / 3600)} jam yang lalu`;
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else {
      setLoading(true);
      setError("");
    }
    try {
      const res = await dashboardApi.getStats();
      setStats(res.data?.data ?? null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat statistik");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Fetch on mount + auto refresh setiap 5 menit
  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => fetchStats(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const now = new Date();
  const greeting =
    now.getHours() < 12
      ? "Selamat Pagi"
      : now.getHours() < 17
        ? "Selamat Siang"
        : "Selamat Malam";
  const riskTotal =
    (stats?.risk_summary?.low ?? 0) +
    (stats?.risk_summary?.moderate ?? 0) +
    (stats?.risk_summary?.high ?? 0);

  if (error)
    return (
      <div className="p-6">
        <ErrorAlert message={error} onRetry={() => fetchStats()} />
      </div>
    );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-display font-800 text-2xl text-gray-900">
            {greeting}, {user?.name?.split(" ")[0] ?? "Admin"} 👋
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {lastUpdate
              ? `Terakhir diperbarui: ${timeAgo(lastUpdate)}`
              : "Memuat data..."}
          </p>
        </div>
        <button
          onClick={() => fetchStats(true)}
          disabled={refreshing || loading}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-600 text-xs font-semibold rounded-xl transition-colors"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
          />
          {refreshing ? "Memperbarui..." : "Refresh"}
        </button>
      </div>

      {/* Group 1: User Stats */}
      <SectionLabel>Pengguna</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        {loading ? (
          [1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <StatCard
              label="Total Terdaftar"
              value={stats?.registered_users}
              icon={Users}
              color="text-primary-700"
              bg="bg-primary-50"
              sub="ibu nifas"
            />
            <StatCard
              label="Pengguna Aktif"
              value={stats?.active_users}
              icon={UserCheck}
              color="text-green-600"
              bg="bg-green-50"
              sub="30 hari terakhir"
            />
            <StatCard
              label="Login Hari Ini"
              value={stats?.today_login}
              icon={LogIn}
              color="text-accent-700"
              bg="bg-accent-50"
              sub="sesi aktif"
            />
            <StatCard
              label="Rata-rata Sesi"
              value={
                stats?.avg_session_minutes != null
                  ? `${stats.avg_session_minutes} mnt`
                  : "—"
              }
              icon={Timer}
              color="text-cyan-600"
              bg="bg-cyan-50"
              sub="per sesi aktif"
            />
          </>
        )}
      </div>

      {/* Group 2: Feature Usage */}
      <SectionLabel>Penggunaan Fitur Hari Ini</SectionLabel>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {loading ? (
          [1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <StatCard
              label="Mood Entries"
              value={stats?.mood_entries}
              icon={Smile}
              color="text-yellow-600"
              bg="bg-yellow-50"
            />
            <StatCard
              label="Data Tidur"
              value={stats?.sleep_entries}
              icon={Moon}
              color="text-indigo-600"
              bg="bg-indigo-50"
            />
            <StatCard
              label="Detak Jantung"
              value={stats?.heart_rate}
              icon={Heart}
              color="text-red-500"
              bg="bg-red-50"
            />
            <StatCard
              label="Tekanan Darah"
              value={stats?.blood_pressure}
              icon={Droplets}
              color="text-primary-700"
              bg="bg-primary-50"
            />
            <StatCard
              label="Smartwatch Sync"
              value={stats?.smartwatch_sync}
              icon={Watch}
              color="text-purple-600"
              bg="bg-purple-50"
            />
            <StatCard
              label="EPDS Selesai"
              value={stats?.epds_completed}
              icon={ClipboardList}
              color="text-teal-600"
              bg="bg-teal-50"
            />
          </>
        )}
      </div>

      {/* Group 3: Risk Summary */}
      <SectionLabel>Ringkasan Risiko</SectionLabel>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-700 text-gray-900">
              Distribusi Risk Level
            </h3>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <TrendingUp className="w-3.5 h-3.5" />
              Total {riskTotal} pengguna dinilai
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              {
                label: "Low Risk",
                value: stats?.risk_summary?.low ?? 0,
                color: "text-green-600",
                bg: "bg-green-50",
                border: "border-green-200",
                icon: CheckCircle,
              },
              {
                label: "Moderate Risk",
                value: stats?.risk_summary?.moderate ?? 0,
                color: "text-yellow-600",
                bg: "bg-yellow-50",
                border: "border-yellow-200",
                icon: AlertTriangle,
              },
              {
                label: "High Risk",
                value: stats?.risk_summary?.high ?? 0,
                color: "text-red-600",
                bg: "bg-red-50",
                border: "border-red-200",
                icon: ShieldAlert,
              },
            ].map((s) => (
              <div
                key={s.label}
                className={`rounded-2xl border p-4 ${s.bg} ${s.border}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                  <span className={`font-display font-800 text-2xl ${s.color}`}>
                    {s.value}
                  </span>
                </div>
                <p className={`text-xs font-semibold ${s.color}`}>{s.label}</p>
                {riskTotal > 0 && (
                  <p className={`text-xs opacity-70 mt-0.5 ${s.color}`}>
                    {Math.round((s.value / riskTotal) * 100)}% dari total
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Stacked bar */}
          {riskTotal > 0 && (
            <>
              <div className="w-full h-3 rounded-full overflow-hidden flex">
                {stats?.risk_summary?.low > 0 && (
                  <div
                    className="bg-green-500 transition-all"
                    style={{
                      width: `${(stats.risk_summary.low / riskTotal) * 100}%`,
                    }}
                  />
                )}
                {stats?.risk_summary?.moderate > 0 && (
                  <div
                    className="bg-yellow-400 transition-all"
                    style={{
                      width: `${(stats.risk_summary.moderate / riskTotal) * 100}%`,
                    }}
                  />
                )}
                {stats?.risk_summary?.high > 0 && (
                  <div
                    className="bg-red-500 transition-all"
                    style={{
                      width: `${(stats.risk_summary.high / riskTotal) * 100}%`,
                    }}
                  />
                )}
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                <span>🟢 Low</span>
                <span>🟡 Moderate</span>
                <span>🔴 High</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
